/** 媒体上传:检录照 / 抓拍 / 申诉材料;dataURL → base64 JSON → key(同一把 key 以后换 OSS 也能取回) */
import type { ApiClient } from './client.js';

/** brand = 品牌图(机构 Logo、大赛官网头图):只收 JPG / PNG / WebP、≤ 2 MB,传上去公开可读 */
export type MediaPurpose = 'checkin_photo' | 'capture' | 'appeal' | 'attachment' | 'subtitle' | 'brand';
/** 分片上传的用途:作品 / 大附件 / 讲解视频原片 */
/** dh_model = 数字人 3D 形象模型(GLB;二期「数字人增强」) */
export type ChunkPurpose = 'work' | 'attachment' | 'lecture_video' | 'dh_model';
export interface MediaRef {
  key: string;
  size: number;
  contentType: string;
  url: string;
}

/** 视频转码任务(讲解视频):排队 / 转码中 n% / 完成带限时播放地址 / 失败带原因 */
export interface TranscodeView {
  id: string;
  status: 'queued' | 'running' | 'done' | 'failed';
  progress: number;
  profile: string;
  sourceKey: string;
  outputKey: string | null;
  posterKey: string | null;
  durationSec: number | null;
  width: number | null;
  height: number | null;
  error: string | null;
  attempts: number;
  createdAt: string;
  finishedAt: string | null;
  playUrl: string | null;
  posterUrl: string | null;
}

/** Uint8Array → base64(分块拼接,避免大数组展开爆栈) */
export function toBase64(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}

// ── 监考影像直传(检录照 / 定时抓拍 / 第三视角 / 录屏片段四处共用) ──

/** 直传票(和后端 DirectUploadService 的 UploadTicket 一致);relay = 这次不发直传票,直接走老的接口中转 */
export type UploadTicket =
  | {
      mode: 'direct';
      key: string;
      /** POST = 表单直传(fields 逐项进表单,文件放最后);PUT = 请求体就是文件 */
      method: 'POST' | 'PUT';
      /** 存储的完整地址,或本服务的相对地址(本地盘中转) */
      url: string;
      headers: Record<string, string>;
      fields: Record<string, string> | null;
      maxBytes: number;
      expiresAt: string;
    }
  | { mode: 'relay'; reason: string };

export type DirectResult<R> =
  { via: 'direct'; key: string } | { via: 'relay'; result: R } | { via: 'failed'; error: unknown };

export interface DirectOptions<R> {
  blob: Blob;
  /** 要票:各链路自己的接口(服务端在发票时做身份与状态校验) */
  ticket: () => Promise<UploadTicket>;
  /** 老的接口中转(base64 JSON):直传走不通时退回来走一次 */
  relay: () => Promise<R>;
  /** 把相对地址拼成接口地址(本地盘的票给的是 /v1/media/direct/…) */
  resolve?: (path: string) => string;
  /** 单次上传多久算超时(默认 15 秒起,每 MB 加 20 秒,最多 2 分钟) */
  timeoutMs?: number;
  /** 直传失败再试几次(默认 2) */
  retries?: number;
  fetchImpl?: typeof fetch;
  /** 测试用:时钟与等待 */
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
}

/** 存储明确拒收(策略不符 / 超大 / 票过期):再试也是一样的结果,不重试 */
class DirectRejected extends Error {}

/** 直传走不通(存储域名被学校网络拦了 / 桶没配跨域 / 超时)之后这么久之内不再试直传,直接走中转 */
export const DIRECT_BREAK_MS = 5 * 60_000;
let directBrokenUntil = 0;
export function resetDirectBreaker(): void {
  directBrokenUntil = 0;
}

async function sendToTicket<R>(
  t: Extract<UploadTicket, { mode: 'direct' }>,
  o: DirectOptions<R>,
): Promise<void> {
  const fetchImpl = o.fetchImpl ?? fetch;
  const sleep = o.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  const retries = o.retries ?? 2;
  const timeoutMs = o.timeoutMs ?? Math.min(120_000, 15_000 + (o.blob.size / 1024 / 1024) * 20_000);
  const url = t.url.startsWith('/') ? (o.resolve?.(t.url) ?? t.url) : t.url;
  for (let attempt = 0; ; attempt++) {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), timeoutMs);
    try {
      let body: BodyInit = o.blob;
      if (t.fields) {
        const form = new FormData();
        for (const [k, v] of Object.entries(t.fields)) form.append(k, v);
        form.append('file', o.blob);
        body = form;
      }
      // 直接传给存储:不带登录令牌、不带 cookie(授权就在票给的地址 / 表单签名里)
      const res = await fetchImpl(url, {
        method: t.method,
        headers: t.headers,
        body,
        signal: ctrl.signal,
        credentials: 'omit',
      });
      // 409 = 这把 key 上已经有文件了(上一次其实传成功、只是没等到回应):当成功,对不对由认领时验
      if (res.ok || res.status === 409) return;
      if (res.status >= 400 && res.status < 500 && res.status !== 408 && res.status !== 429)
        throw new DirectRejected(`存储拒收(HTTP ${res.status})`);
      throw new Error(`直传失败(HTTP ${res.status})`);
    } catch (e) {
      if (e instanceof DirectRejected || attempt >= retries) throw e;
      await sleep(500 * (attempt + 1));
    } finally {
      clearTimeout(timer);
    }
  }
}

/**
 * 影像上传的公共函数:要票 → 直接传到存储(带超时,失败重试 2 次)→ 返回 key;
 * 直传走不通(票要不到 / 存储域名被拦 / 跨域没配 / 超时)自动退回老的接口中转一次,并在 5 分钟内不再试直传。
 * 不抛错:全都失败返回 via = failed,由调用方按各自原来的方式处理——任何影像传不上去都不能挡住考试和交卷。
 */
export async function uploadDirect<R>(o: DirectOptions<R>): Promise<DirectResult<R>> {
  const now = o.now ?? Date.now;
  if (o.blob.size > 0 && now() >= directBrokenUntil) {
    let ticket: UploadTicket | null = null;
    try {
      ticket = await o.ticket();
    } catch {
      // 票没要到(被业务拒 / 太频繁 / 网络):这一次走中转,由中转那条路给出该有的结果;不算直传坏了
    }
    if (ticket?.mode === 'direct' && o.blob.size <= ticket.maxBytes) {
      try {
        await sendToTicket(ticket, o);
        return { via: 'direct', key: ticket.key };
      } catch {
        directBrokenUntil = now() + DIRECT_BREAK_MS;
      }
    }
  }
  try {
    return { via: 'relay', result: await o.relay() };
  } catch (error) {
    return { via: 'failed', error };
  }
}

/** dataURL → Blob(直传传的是原始字节,不是 base64) */
export function dataUrlToBlob(dataUrl: string): Blob {
  const comma = dataUrl.indexOf(',');
  const type = /^data:([^;,]+)/.exec(dataUrl.slice(0, comma))?.[1] ?? 'application/octet-stream';
  const bin = atob(dataUrl.slice(comma + 1));
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type });
}

export function mediaApi(api: ApiClient) {
  return {
    /** 直传(监考影像四条链路共用):要票 → 传到存储 → 返回 key;走不通退回调用方给的老中转;不抛错 */
    direct: <R>(o: Pick<DirectOptions<R>, 'blob' | 'ticket' | 'relay'>) =>
      uploadDirect<R>({ ...o, resolve: api.resolve }),
    /**
     * 检录照 / 抓拍:先直传存储,走不通退回 base64 中转;返回媒体 key 与字节数。
     * 两条路都失败才抛错(页面照原来的方式处理:检录照提示重试,抓拍记一次失败,都不拦考试)。
     */
    async uploadProctor(
      purpose: 'checkin_photo' | 'capture',
      dataUrl: string,
      ticket: (meta: { contentType: string; size: number }) => Promise<UploadTicket>,
    ): Promise<{ key: string; size: number; via: 'direct' | 'relay' }> {
      const blob = dataUrlToBlob(dataUrl);
      const r = await uploadDirect<MediaRef>({
        blob,
        resolve: api.resolve,
        ticket: () => ticket({ contentType: blob.type, size: blob.size }),
        relay: () => mediaApi(api).uploadDataUrl(purpose, dataUrl),
      });
      if (r.via === 'direct') return { key: r.key, size: blob.size, via: 'direct' };
      if (r.via === 'relay') return { key: r.result.key, size: r.result.size, via: 'relay' };
      throw r.error;
    },
    uploadDataUrl(purpose: MediaPurpose, dataUrl: string, name?: string) {
      const comma = dataUrl.indexOf(',');
      const head = dataUrl.slice(0, comma);
      const contentType = /^data:([^;,]+)/.exec(head)?.[1] ?? 'application/octet-stream';
      return api.post<MediaRef>('/v1/media', {
        purpose,
        contentType,
        base64: dataUrl.slice(comma + 1),
        ...(name ? { name } : {}),
      });
    },
    uploadFile(purpose: MediaPurpose, file: File, contentType?: string) {
      return new Promise<MediaRef>((resolve, reject) => {
        if (file.size === 0) {
          reject(new Error('空文件直接拒收'));
          return;
        }
        const reader = new FileReader();
        reader.onerror = () => reject(new Error('读取文件失败'));
        reader.onload = () => {
          const base64 = String(reader.result).split(',')[1] ?? '';
          api
            .post<MediaRef>('/v1/media', {
              purpose,
              contentType: contentType ?? (file.type || 'application/octet-stream'),
              base64,
              name: file.name,
            })
            .then(resolve, reject);
        };
        reader.readAsDataURL(file);
      });
    },
    /** 大文件分片上传:按服务端给的分片大小顺序传,返回媒体 key;onProgress 给 0–1 */
    async uploadChunked(
      purpose: ChunkPurpose,
      file: File,
      onProgress?: (ratio: number) => void,
    ): Promise<string> {
      if (file.size === 0) throw new Error('空文件(0 KB),请检查后重新选择');
      const init = await api.post<{ uploadId: string; chunkBytes: number; parts: number }>(
        '/v1/media/uploads',
        {
          purpose,
          name: file.name,
          size: file.size,
        },
      );
      let n = 0;
      for (let off = 0; off < file.size; off += init.chunkBytes, n++) {
        const buf = new Uint8Array(await file.slice(off, off + init.chunkBytes).arrayBuffer());
        await api.put(`/v1/media/uploads/${init.uploadId}/parts/${n}`, { base64: toBase64(buf) });
        onProgress?.(Math.min(1, (off + buf.length) / file.size));
      }
      const done = await api.post<{ key: string }>(`/v1/media/uploads/${init.uploadId}/complete`, {});
      return done.key;
    },
    /** 讲解视频排队转码(同一个原片转过就复用) */
    transcode: (key: string) => api.post<TranscodeView>('/v1/media/transcodes', { key, profile: 'lecture' }),
    transcodeStatus: (id: string) => api.get<TranscodeView>(`/v1/media/transcodes/${id}`),
  };
}
export type MediaApi = ReturnType<typeof mediaApi>;
